"use client";

import { useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map(item => <NavigationMenuItem key={item.id} data-index={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground hover:text-primary transition-all duration-300 text-sm font-medium tracking-widest uppercase relative py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-4">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-foreground hover:text-primary transition-colors transition-default font-default text-lg font-medium" data-index={item.id}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function CompanyHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="257915" id="header" className="bg-background text-foreground">
      <header className={`sticky top-0 z-40 w-full border-b border-border bg-white transition-shadow duration-300 ${isScrolled ? "shadow-sm" : ""}`}>
        <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to="/">
                <WvcLogo className="h-8 w-auto text-secondary" />
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <MenuProvider menu_id={39}>
                <HeaderMenuConsumer />
              </MenuProvider>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all rounded-md font-medium text-sm tracking-wide">
                Download App
              </Button>
            </div>

            <div className="flex md:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground">
                    {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64 bg-background border-l border-border">
                  <MenuProvider menu_id={39}>
                    <MobileMenuConsumer />
                  </MenuProvider>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-20 sm:py-24 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-2">
            <div className="flex flex-col justify-center">
              <div className="mb-6 inline-flex items-center gap-2 w-fit">
                <span className="text-sm font-semibold text-primary tracking-widest uppercase">
                  The Concierge of Mobility
                </span>
              </div>

              <h1 className="font-serif mb-4 text-4xl font-medium text-foreground sm:text-5xl lg:text-7xl tracking-tight leading-tight">
                Redefining Premium Travel
              </h1>

              <p className="mb-6 text-base text-muted-foreground sm:text-lg font-default leading-relaxed">
                Moveonix brings a new standard of luxury to ride-hailing. Experience seamless, sophisticated transportation designed for those who value time, safety, and unparalleled comfort.
              </p>

              <div className="mb-8">
                <h2 className="font-default mb-4 text-xl font-semibold text-foreground tracking-wide">
                  Our Global Presence
                </h2>
                <p className="text-base text-muted-foreground font-default">
                  Headquartered in Delaware, Moveonix is expanding rapidly across the globe. We combine local expertise with international standards to deliver a world-class mobility experience.
                </p>
              </div>

              <div className="mb-8">
                <h3 className="font-default mb-4 text-xl font-semibold text-foreground tracking-wide">
                  The Moveonix Standard
                </h3>
                <ul className="space-y-3 text-base text-muted-foreground font-default">
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-lg bg-primary mt-2"></span>
                    <span>Uncompromising Safety Protocols</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-lg bg-primary mt-2"></span>
                    <span>Professional Concierge Drivers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-lg bg-primary mt-2"></span>
                    <span>Premium Fleet Excellence</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <div className="mb-8 w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_premium_fleet.png" alt="Moveonix Premium Vehicle" className="h-auto w-full object-contain rounded-md shadow-xl brightness-105" loading="lazy" decoding="async" />
              </div>

              <div className="w-full rounded-md border border-border bg-card p-8 shadow-md">
                <h3 className="font-serif mb-4 text-2xl font-medium text-foreground">
                  Global Rollout Status
                </h3>
                <p className="mb-6 text-sm text-muted-foreground font-default">
                  We are bringing the Moveonix experience to key markets worldwide. Track our progress as we launch in new regions.
                </p>

                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <h4 className="font-default text-sm font-semibold text-foreground">
                      Georgia
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-chart-2/10 text-chart-2 uppercase tracking-widest">
                      Opened
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <h4 className="font-default text-sm font-semibold text-foreground">
                      Egypt & Philippines
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-muted text-muted-foreground uppercase tracking-widest">
                      Coming Soon
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-border pb-2">
                    <h4 className="font-default text-sm font-semibold text-foreground">
                      Uzbekistan & Kenya
                    </h4>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-muted text-muted-foreground uppercase tracking-widest">
                      Coming Soon
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}